// Invite links, share messages and the race details shown on the welcome page.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Marks invite links so the visits and sign-ups they bring in can be counted.
// Checkout keeps utm_* params all the way to the confirmation page.
export const INVITE_SOURCE = "invite";

export function isInviteVisit(search = "") {
  return new URLSearchParams(search).get("utm_source") === INVITE_SOURCE;
}

// Sends friends straight to this challenge's registration page, with the member's referral code when they have one
// and the race's discount coupon (applied automatically at checkout).
export function buildInviteUrl(origin, raceName, referralCode, promoCode) {
  const url = new URL(raceName ? `/register/${raceName}` : "/register", origin);
  if (referralCode) url.searchParams.set("referral_link", referralCode);
  if (promoCode) url.searchParams.set("promo_code", promoCode);
  url.searchParams.set("utm_source", INVITE_SOURCE);
  url.searchParams.set("utm_medium", "share");
  return url.toString();
}

// Date-only strings ("2027-01-01") are read as calendar days so they never shift by time zone.
function toDay(value) {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return { year, month: month - 1, day };
  }
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return { year: date.getFullYear(), month: date.getMonth(), day: date.getDate() };
}

export function formatRaceDates(raceInfo = {}) {
  const localDates = raceInfo?.activityDatePolicy === "participant_local_v1";
  const start = toDay(localDates ? raceInfo?.activityStartDate : raceInfo?.raceDateStart);
  const end = toDay(localDates ? raceInfo?.activityEndDate : raceInfo?.raceDateEnd);
  if (!start || !end) return "";
  if (start.year === end.year && start.month === end.month) {
    return `${MONTHS[start.month]} ${start.day}–${end.day}, ${end.year}`;
  }
  if (start.year === end.year) {
    return `${MONTHS[start.month]} ${start.day} – ${MONTHS[end.month]} ${end.day}, ${end.year}`;
  }
  return `${MONTHS[start.month]} ${start.day}, ${start.year} – ${MONTHS[end.month]} ${end.day}, ${end.year}`;
}

export function formatRaceDistance(raceInfo = {}) {
  const distance = `${raceInfo?.distance ?? ""}`.trim();
  if (distance && /^\d+(\.\d+)?$/.test(distance)) return `${distance} km`;
  return distance || `${raceInfo?.legName ?? ""}`.trim();
}

// "Jan 1" while the challenge hasn't started yet, "" once it has (or when the date is unknown).
export function formatUpcomingStart(raceInfo = {}, now = new Date()) {
  const localDates = raceInfo?.activityDatePolicy === "participant_local_v1";
  const start = toDay(localDates ? raceInfo?.activityStartDate : raceInfo?.raceDateStart);
  if (!start) return "";
  const today = { year: now.getFullYear(), month: now.getMonth(), day: now.getDate() };
  const startKey = start.year * 10000 + start.month * 100 + start.day;
  const todayKey = today.year * 10000 + today.month * 100 + today.day;
  return startKey > todayKey ? `${MONTHS[start.month]} ${start.day}` : "";
}
